import ExcelJS from 'exceljs';
/**
 * Parse an XLSX ArrayBuffer into an array of ParsedSheet objects.
 * Uses ExcelJS for full fidelity — merged cells, column widths,
 * frozen panes, and basic cell formatting are all preserved.
 */ export async function parseExcelBuffer(buffer) {
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
    const sheets = [];
    workbook.eachSheet((worksheet)=>{
        const rowCount = worksheet.rowCount;
        const colCount = worksheet.columnCount;
        // Ensure minimum dimensions for editing comfort.
        const minRows = Math.max(rowCount, 50);
        const minCols = Math.max(colCount, 20);
        // --- Cell values ---
        const data = [];
        for(let r = 1; r <= minRows; r++){
            const row = [];
            const wsRow = worksheet.getRow(r);
            for(let c = 1; c <= minCols; c++){
                const cell = wsRow.getCell(c);
                if (r > rowCount || c > colCount) {
                    row.push(null);
                    continue;
                }
                // Preserve the effective value — ExcelJS resolves formulas to .result
                const val = cell.value;
                if (val === null || val === undefined) {
                    row.push(null);
                } else if (typeof val === 'object') {
                    // Handle rich text, formula results, dates, etc.
                    if ('result' in val && val.result !== undefined) {
                        // Formula cell — use the cached result
                        row.push(formatCellValue(val.result));
                    } else if ('richText' in val && Array.isArray(val.richText)) {
                        // Rich text — flatten to plain string
                        row.push(val.richText.map((rt)=>rt.text).join(''));
                    } else if (val instanceof Date) {
                        row.push(val.toLocaleDateString());
                    } else {
                        row.push(String(val));
                    }
                } else {
                    row.push(val);
                }
            }
            data.push(row);
        }
        // --- Merged cells ---
        const mergeCells = [];
        for (const mergeRange of Object.keys(worksheet.model.merges ?? {})){
            const parsed = parseRange(mergeRange);
            if (parsed) {
                mergeCells.push(parsed);
            }
        }
        // --- Column widths ---
        const colWidths = [];
        for(let c = 1; c <= minCols; c++){
            const col = worksheet.getColumn(c);
            // ExcelJS width is in "characters" — roughly 7px per unit, default ~8.43
            const width = col.width ?? 8.43;
            colWidths.push(Math.round(width * 7));
        }
        // --- Row heights ---
        const rowHeights = [];
        for(let r = 1; r <= minRows; r++){
            const row = worksheet.getRow(r);
            // ExcelJS height is in points — 1pt ≈ 1.33px
            const height = row.height ?? 15;
            rowHeights.push(Math.round(height * 1.33));
        }
        // --- Frozen panes ---
        let frozenRows = 0;
        let frozenCols = 0;
        const views = worksheet.views;
        if (views && views.length > 0) {
            const view = views[0];
            if (view.state === 'frozen') {
                frozenRows = view.ySplit ?? 0;
                frozenCols = view.xSplit ?? 0;
            }
        }
        // --- Basic cell formatting (bold, alignment) ---
        const cellMeta = [];
        for(let r = 1; r <= Math.min(rowCount, 200); r++){
            const wsRow = worksheet.getRow(r);
            for(let c = 1; c <= Math.min(colCount, 50); c++){
                const cell = wsRow.getCell(c);
                const classes = [];
                if (cell.font?.bold) classes.push('htBold');
                if (cell.font?.italic) classes.push('htItalic');
                if (cell.alignment?.horizontal === 'center') classes.push('htCenter');
                if (cell.alignment?.horizontal === 'right') classes.push('htRight');
                if (classes.length > 0) {
                    cellMeta.push({
                        row: r - 1,
                        col: c - 1,
                        className: classes.join(' ')
                    });
                }
            }
        }
        sheets.push({
            name: worksheet.name,
            data,
            mergeCells,
            colWidths,
            rowHeights,
            frozenRows,
            frozenCols,
            cellMeta
        });
    });
    return sheets;
}
function formatCellValue(val) {
    if (val === null || val === undefined) return null;
    if (val instanceof Date) return val.toLocaleDateString();
    if (typeof val === 'object') return String(val);
    return val;
}
/**
 * Parse an Excel range string like "A1:C3" into Handsontable merge format.
 */ function parseRange(range) {
    const match = range.match(/^([A-Z]+)(\d+):([A-Z]+)(\d+)$/);
    if (!match) return null;
    const col1 = colLetterToIndex(match[1]);
    const row1 = Number.parseInt(match[2], 10) - 1;
    const col2 = colLetterToIndex(match[3]);
    const row2 = Number.parseInt(match[4], 10) - 1;
    return {
        row: row1,
        col: col1,
        rowspan: row2 - row1 + 1,
        colspan: col2 - col1 + 1
    };
}
function colLetterToIndex(letters) {
    let index = 0;
    for(let i = 0; i < letters.length; i++){
        index = index * 26 + (letters.charCodeAt(i) - 64);
    }
    return index - 1; // 0-based
}
